/**
 * Billing V2 plan catalog — the single source of truth for the plan cards
 * shown in both the Settings → Billing page and the post-signup onboarding
 * paywall. Prices/features are intentionally static here; the backend remains
 * authoritative for the actual charged amount (getPlanAmount + promotions).
 */

export interface PlanFeature {
  category: 'store' | 'marketing' | 'payments' | 'shipping'
  feature: string
  value: string | boolean
}

export interface Plan {
  id: string
  name: { en: string; ar: string }
  price: number
  badge: string
  description: string
  duration: 'monthly' | 'oneTime' | 'trial' | 'free'
  isPopular: boolean
  features: PlanFeature[]
}

/** Plan ids the merchant can actually purchase (excludes `preview`). */
export const SELLABLE_PLAN_IDS = ['launch', 'pro', 'scale'] as const

/** Feature category order used to group the plan-card feature lists. */
export const PLAN_FEATURE_CATEGORIES: PlanFeature['category'][] = ['store', 'marketing', 'payments', 'shipping']

const CATEGORY_FEATURES: Record<string, string[]> = {
  store: ['products', 'orders', 'staffAccounts', 'themesCount', 'domains', 'emailDomain', 'themeBuilder', 'migrationTool'],
  marketing: [
    'marketingCredits',
    'abandonedCartTracking',
    'whatsappAutomations',
    'winBack',
    'reviewAutomation',
    'segmentCampaigns',
    'advancedDiscounts',
    'automaticDiscounts',
    'crossSellUpsell',
    'affiliates',
  ],
  payments: ['transactionFees', 'localPayments', 'bnplPayments', 'conversionTracking'],
  shipping: ['shippingIntegrations', 'publicApiAccess', 'mcpAccess', 'scheduledExports', 'supportSla'],
}

/** Maps a raw feature value to its i18n key (for non-boolean display values). */
const FEATURE_VALUE_TRANSLATIONS: Record<string, string> = {
  unlimited: 'billing.featureValues.unlimited',
  customAndSubdomain: 'billing.featureValues.customAndSubdomain',
  advanced: 'billing.featureValues.advanced',
  full: 'billing.featureValues.full',
  earlyFounder: 'billing.featureValues.earlyFounder',
  '1000orders': 'billing.featureValues.1000orders',
  '5000orders': 'billing.featureValues.5000orders',
  '1market': 'billing.featureValues.1market',
  limited: 'billing.featureValues.limited',
  limited20k: 'billing.featureValues.limited20k',
  subdomain: 'billing.featureValues.subdomain',
  basic: 'billing.featureValues.basic',
  soon: 'billing.featureValues.soon',
  taketheme: 'billing.featureValues.takethemeEmail',
  brand: 'billing.featureValues.brandEmail',
  zeroFees: 'billing.featureValues.zeroFees',
  sla48h: 'billing.featureValues.sla48h',
  sla12h: 'billing.featureValues.sla12h',
  sla2h: 'billing.featureValues.sla2h',
}

/** Localized display name of a catalog plan id, or null for ids the catalog doesn't know (legacy tiers). */
export function getPlanName(planId: string, lang: string): string | null {
  const plan = PLAN_CATALOG.find(p => p.id === planId)
  if (!plan) return null
  return lang === 'ar' ? plan.name.ar : plan.name.en
}

export function getCategoryFeatures(category: string): string[] {
  return CATEGORY_FEATURES[category] || []
}

export function getFeatureValue(plan: Plan, feature: string): string | boolean | undefined {
  return plan.features.find(f => f.feature === feature)?.value
}

/**
 * Renders a feature value for display. Booleans become check/cross marks;
 * known string values are translated via the supplied translate function;
 * anything else is shown verbatim.
 */
export function formatFeatureValue(value: string | boolean | undefined, translate: (key: string) => string): string {
  if (typeof value === 'boolean') return value ? '✓' : '✕'
  if (value && FEATURE_VALUE_TRANSLATIONS[value]) return translate(FEATURE_VALUE_TRANSLATIONS[value])
  return (value as string) ?? ''
}

export const PLAN_CATALOG: Plan[] = [
  {
    id: 'launch',
    name: { en: 'Launch', ar: 'الانطلاق' },
    price: 599,
    badge: 'billing.starterBadge',
    description: 'billing.launchPlanDescription',
    duration: 'monthly',
    isPopular: false,
    features: [
      { category: 'store', feature: 'products', value: 'unlimited' },
      { category: 'store', feature: 'orders', value: 'unlimited' },
      { category: 'store', feature: 'staffAccounts', value: '3' },
      { category: 'store', feature: 'themesCount', value: '2' },
      { category: 'store', feature: 'domains', value: 'customAndSubdomain' },
      { category: 'store', feature: 'emailDomain', value: 'taketheme' },
      { category: 'store', feature: 'themeBuilder', value: true },
      { category: 'store', feature: 'migrationTool', value: false },
      { category: 'marketing', feature: 'marketingCredits', value: '1,000' },
      { category: 'marketing', feature: 'abandonedCartTracking', value: false },
      { category: 'marketing', feature: 'whatsappAutomations', value: false },
      { category: 'marketing', feature: 'winBack', value: false },
      { category: 'marketing', feature: 'reviewAutomation', value: false },
      { category: 'marketing', feature: 'segmentCampaigns', value: false },
      { category: 'marketing', feature: 'advancedDiscounts', value: false },
      { category: 'marketing', feature: 'automaticDiscounts', value: false },
      { category: 'marketing', feature: 'crossSellUpsell', value: false },
      { category: 'marketing', feature: 'affiliates', value: false },
      { category: 'payments', feature: 'transactionFees', value: 'zeroFees' },
      { category: 'payments', feature: 'localPayments', value: true },
      { category: 'payments', feature: 'bnplPayments', value: true },
      { category: 'payments', feature: 'conversionTracking', value: true },
      { category: 'shipping', feature: 'shippingIntegrations', value: true },
      { category: 'shipping', feature: 'publicApiAccess', value: false },
      { category: 'shipping', feature: 'mcpAccess', value: true },
      { category: 'shipping', feature: 'scheduledExports', value: false },
      { category: 'shipping', feature: 'supportSla', value: 'sla48h' },
    ],
  },
  {
    id: 'pro',
    name: { en: 'Pro', ar: 'برو' },
    price: 1250,
    badge: 'billing.proBadge',
    description: 'billing.proPlanDescription',
    duration: 'monthly',
    isPopular: true,
    features: [
      { category: 'store', feature: 'products', value: 'unlimited' },
      { category: 'store', feature: 'orders', value: 'unlimited' },
      { category: 'store', feature: 'staffAccounts', value: '10' },
      { category: 'store', feature: 'themesCount', value: '5' },
      { category: 'store', feature: 'domains', value: 'customAndSubdomain' },
      { category: 'store', feature: 'emailDomain', value: 'brand' },
      { category: 'store', feature: 'themeBuilder', value: true },
      { category: 'store', feature: 'migrationTool', value: true },
      { category: 'marketing', feature: 'marketingCredits', value: '5,000' },
      { category: 'marketing', feature: 'abandonedCartTracking', value: true },
      { category: 'marketing', feature: 'whatsappAutomations', value: true },
      { category: 'marketing', feature: 'winBack', value: true },
      { category: 'marketing', feature: 'reviewAutomation', value: true },
      { category: 'marketing', feature: 'segmentCampaigns', value: true },
      { category: 'marketing', feature: 'advancedDiscounts', value: true },
      { category: 'marketing', feature: 'automaticDiscounts', value: true },
      { category: 'marketing', feature: 'crossSellUpsell', value: true },
      { category: 'marketing', feature: 'affiliates', value: false },
      { category: 'payments', feature: 'transactionFees', value: 'zeroFees' },
      { category: 'payments', feature: 'localPayments', value: true },
      { category: 'payments', feature: 'bnplPayments', value: true },
      { category: 'payments', feature: 'conversionTracking', value: true },
      { category: 'shipping', feature: 'shippingIntegrations', value: true },
      { category: 'shipping', feature: 'publicApiAccess', value: true },
      { category: 'shipping', feature: 'mcpAccess', value: true },
      { category: 'shipping', feature: 'scheduledExports', value: true },
      { category: 'shipping', feature: 'supportSla', value: 'sla12h' },
    ],
  },
  {
    id: 'scale',
    name: { en: 'Scale', ar: 'التوسع' },
    price: 3450,
    badge: 'billing.scaleBadge',
    description: 'billing.scalePlanDescription',
    duration: 'monthly',
    isPopular: false,
    features: [
      { category: 'store', feature: 'products', value: 'unlimited' },
      { category: 'store', feature: 'orders', value: 'unlimited' },
      { category: 'store', feature: 'staffAccounts', value: '20' },
      { category: 'store', feature: 'themesCount', value: 'unlimited' },
      { category: 'store', feature: 'domains', value: 'customAndSubdomain' },
      { category: 'store', feature: 'emailDomain', value: 'brand' },
      { category: 'store', feature: 'themeBuilder', value: true },
      { category: 'store', feature: 'migrationTool', value: true },
      { category: 'marketing', feature: 'marketingCredits', value: '15,000' },
      { category: 'marketing', feature: 'abandonedCartTracking', value: true },
      { category: 'marketing', feature: 'whatsappAutomations', value: true },
      { category: 'marketing', feature: 'winBack', value: true },
      { category: 'marketing', feature: 'reviewAutomation', value: true },
      { category: 'marketing', feature: 'segmentCampaigns', value: true },
      { category: 'marketing', feature: 'advancedDiscounts', value: true },
      { category: 'marketing', feature: 'automaticDiscounts', value: true },
      { category: 'marketing', feature: 'crossSellUpsell', value: true },
      { category: 'marketing', feature: 'affiliates', value: true },
      { category: 'payments', feature: 'transactionFees', value: 'zeroFees' },
      { category: 'payments', feature: 'localPayments', value: true },
      { category: 'payments', feature: 'bnplPayments', value: true },
      { category: 'payments', feature: 'conversionTracking', value: true },
      { category: 'shipping', feature: 'shippingIntegrations', value: true },
      { category: 'shipping', feature: 'publicApiAccess', value: true },
      { category: 'shipping', feature: 'mcpAccess', value: true },
      { category: 'shipping', feature: 'scheduledExports', value: true },
      { category: 'shipping', feature: 'supportSla', value: 'sla2h' },
    ],
  },
]
